import firebase from "firebase/compat/app";
import { httpsCallable } from "firebase/functions";
import {
    AssignmentConfiguration,
    AssignmentEvent,
    AssignmentMode,
    CoordinatorTimeOff,
} from "intake_shared/src/assignment";
import db, { fxs } from "../utils/firestore";

export type TimeOffRange = CoordinatorTimeOff & { id: string };

function timeOffCollection(email: string) {
    return db
        .collection("acl")
        .doc(email.trim().toLowerCase())
        .collection("time_off");
}

export async function getCoordinatorTimeOff(
    email: string,
    source?: "server"
): Promise<TimeOffRange[]> {
    const collection = timeOffCollection(email);
    const snapshot = source
        ? await collection.get({ source })
        : await collection.get();
    return snapshot.docs
        .map((entry) => ({ ...entry.data(), id: entry.id } as TimeOffRange))
        .sort((left, right) => left.start.localeCompare(right.start));
}

export async function getCoordinatorTimeOffForBalance(
    email: string,
    periodStart: string
): Promise<TimeOffRange[]> {
    const snapshot = await timeOffCollection(email)
        .where("end", ">=", periodStart)
        .get();
    return snapshot.docs
        .map((entry) => ({ ...entry.data(), id: entry.id } as TimeOffRange))
        .sort((left, right) => left.start.localeCompare(right.start));
}

export async function addCoordinatorTimeOff(
    email: string,
    actor: string,
    range: Pick<CoordinatorTimeOff, "start" | "end" | "note">
) {
    await timeOffCollection(email).add({
        start: range.start,
        end: range.end,
        ...(range.note?.trim() ? { note: range.note.trim() } : {}),
        created_by: actor.trim().toLowerCase(),
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
    });
}

export async function deleteCoordinatorTimeOff(email: string, id: string) {
    await timeOffCollection(email).doc(id).delete();
}

export async function getAssignmentConfiguration(): Promise<AssignmentConfiguration> {
    const document = await db.collection("comm_config").doc("assignment").get();
    return {
        mode: document.data()?.mode === "weighted" ? "weighted" : "round_robin",
        last_score: Number(document.data()?.last_score) || 0,
    };
}

export async function setAssignmentMode(mode: AssignmentMode) {
    await httpsCallable(fxs, "httpFunctionAssignmentSettings")({ mode });
}

export async function getAssignmentEvents(since: Date): Promise<AssignmentEvent[]> {
    const snapshot = await db
        .collection("assignment_events")
        .where("created_at", ">=", since)
        .get();
    return snapshot.docs.map((entry) => entry.data() as AssignmentEvent);
}
